import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../utils/currency';

export function PaymentSetup({
	title,
	lastValidDate,
	currency,
	price,
	interestRate,
	paymentType,
	islandNo,
	parselNo,
	blokNo,
	updateFields,
}) {
	const [islands, setIslands] = useState([]);
	const [selectedIsland, setSelectedIsland] = useState('');
	const [areas, setAreas] = useState([]);
	const [selectedArea, setSelectedArea] = useState('');
	const [blocks, setBlocks] = useState([]);
	const [selectedBlock, setSelectedBlock] = useState('');
	const [blockPrice, setBlockPrice] = useState('');
	const [exchangeRates, setExchangeRates] = useState([]);
	const [initialPrice, setInitialPrice] = useState(null);
	const [maxValidityDays, setMaxValidityDays] = useState(30); // Default to 30 days

	// Fetch islands
	useEffect(() => {
		fetch('/api/islands')
			.then((res) => res.json())
			.then((data) => {
				// Hide sold buildings, and any area/island left with nothing to offer
				const available = data
					.map((island) => ({
						...island,
						areas: island.areas
							.map((area) => ({
								...area,
								buildings: area.buildings.filter((b) => !b.sold),
							}))
							.filter((area) => area.buildings.length > 0),
					}))
					.filter((island) => island.areas.length > 0);
				setIslands(available);
			})
			.catch((error) => console.error('Error fetching islands:', error));
	}, []);

	// Fetch exchange rates
	useEffect(() => {
		fetch('/api/exchange-rate')
			.then((res) => res.json())
			.then((data) => setExchangeRates(data))
			.catch((error) => console.error('Error fetching exchange rates:', error));
	}, []);

	// Fetch app settings for max validity days
	useEffect(() => {
		fetch('/api/app-settings')
			.then((res) => res.json())
			.then((data) => {
				if (data?.maxValidityDays) {
					setMaxValidityDays(data.maxValidityDays);
				}
			})
			.catch((error) => console.error('Error fetching app settings:', error));
	}, []);

	useEffect(() => {
		if (initialPrice === null && price !== '') {
			setInitialPrice(price);
		}
	}, [price, initialPrice]);

	useEffect(() => {
		// Ensure price is updated only when a block is selected, or currency changes.
		if (!currency || exchangeRates.length === 0 || initialPrice === null)
			return;

		let selectedRate = 1;
		if (currency === 'TRY') {
			selectedRate =
				exchangeRates.find((rate) => rate.currency === 'USD')?.rate || 1;
		} else if (currency === 'EUR') {
			let usdRate =
				exchangeRates.find((rate) => rate.currency === 'USD')?.rate || 1;
			let euroRate =
				exchangeRates.find((rate) => rate.currency === 'EUR')?.rate || 1;
			selectedRate = usdRate / euroRate;
		}

		// If price from block is available, convert it using exchange rate
		if (blockPrice) {
			const newConvertedPrice = (parseFloat(blockPrice) * selectedRate).toFixed(
				2
			);
			updateFields({ price: newConvertedPrice });
		} else if (initialPrice) {
			// If block price isn't available, fall back to initial price conversion
			const newConvertedPrice = (
				parseFloat(initialPrice) * selectedRate
			).toFixed(2);
			updateFields({ price: newConvertedPrice });
		}
	}, [currency, exchangeRates, blockPrice, initialPrice, updateFields]);

	// Update areas when an island is selected
	useEffect(() => {
		if (selectedIsland) {
			const island = islands.find((i) => i.id === selectedIsland);
			if (island) {
				const areaList = island.areas.map((area) => area.areaId);
				setAreas(areaList);
				setBlocks([]); // Reset blocks when island changes
				setBlockPrice(''); // Reset price when island changes
			}
		} else {
			setAreas([]);
			setBlocks([]);
			setBlockPrice('');
		}
	}, [selectedIsland, islands]);

	// Update blocks when an area is selected
	useEffect(() => {
		if (selectedIsland && selectedArea) {
			const island = islands.find((i) => i.id === selectedIsland);
			if (island) {
				const area = island.areas.find(
					(a) => a.areaId === Number(selectedArea)
				);
				if (area) {
					const blockList = area.buildings.map((b) => b.name);
					setBlocks(blockList);
				}
			}
		} else {
			setBlocks([]);
			setBlockPrice('');
		}
	}, [selectedArea, selectedIsland, islands]);

	// Update price when a block is selected
	useEffect(() => {
		if (selectedIsland && selectedArea && selectedBlock) {
			const island = islands.find((i) => i.id === selectedIsland);
			if (island) {
				const area = island.areas.find(
					(a) => a.areaId === Number(selectedArea)
				);
				if (area) {
					const block = area.buildings.find((b) => b.name === selectedBlock);
					if (block) {
						setBlockPrice(block.value); // Set the price from the block
						updateFields({ price: block.value }); // Update the price field
					}
				}
			}
		} else {
			setBlockPrice('');
		}
	}, [selectedBlock, selectedArea, selectedIsland, islands]);

	useEffect(() => {
		if (currency) {
			const selectedRate = exchangeRates.find(
				(rate) => rate.currency === currency
			);
			if (selectedRate && selectedRate.interest !== interestRate) {
				updateFields({ interestRate: selectedRate.interest });
			}
		}
	}, [currency, exchangeRates, interestRate, updateFields]);

	return (
		<form className="create" autoComplete="off">
			<h3>Yeni Sözleşme Ekle</h3>

			<label>Sözleşme Adı:</label>
			<div className="custom-input">
				<input
					type="text"
					value={title}
					onChange={(e) => updateFields({ title: e.target.value })}
					placeholder='Örn: "Kira Sözleşmesi"'
				/>
			</div>

			{/* Island Selection */}
			<label>Ada Adı:</label>
			<div className="custom-select">
				<select
					value={selectedIsland}
					onChange={(e) => {
						setSelectedIsland(e.target.value);
						updateFields({ islandNo: e.target.value });
					}}
				>
					<option value="" disabled>
						Bir ada seçin
					</option>
					{islands.map((island) => (
						<option key={island.id} value={island.id}>
							{island.id}
						</option>
					))}
				</select>
			</div>

			{/* Area Selection */}
			<label>Parsel Adı:</label>
			<div className="custom-select">
				<select
					type="text"
					value={selectedArea}
					onChange={(e) =>
						setSelectedArea(e.target.value) ||
						updateFields({ parselNo: e.target.value })
					}
				>
					<option value="" disabled>
						Bir parsel seçin
					</option>

					{areas.map((area) => (
						<option key={area} value={area}>
							{area}
						</option>
					))}
				</select>
			</div>

			{/* Block Selection */}
			<label>Blok No:</label>
			<div className="custom-select">
				<select
					type="text"
					value={selectedBlock}
					onChange={(e) =>
						setSelectedBlock(e.target.value) ||
						updateFields({ blokNo: e.target.value })
					}
				>
					<option value="" disabled>
						Bir blok seçin
					</option>
					{blocks.map((block) => (
						<option key={block} value={block}>
							{block}
						</option>
					))}
				</select>
			</div>

			<label>Sözleşme Döviz:</label>
			<div className="custom-select">
				<select
					value={currency}
					onChange={(e) => updateFields({ currency: e.target.value })}
				>
					<option value="" disabled>
						Bir para birimi seçin
					</option>
					<option value="USD">USD</option>
					<option value="TRY">TRY</option>
					<option value="EUR">EUR</option>
				</select>
			</div>

			{/* Price Field */}
			<label>Sözleşme Bedeli:</label>
			<div className="custom-input">
				<input
					type="text"
					value={formatCurrency(price, currency)}
					readOnly
					placeholder="Otomatik atanır"
				/>
			</div>

			<label>Teklif Geçerlilik Tarihi:</label>
			<div className="custom-input">
				<input
					type="date"
					name="date"
					value={lastValidDate}
					min={new Date().toISOString().split('T')[0]}
					max={(() => {
						const maxDate = new Date();
						maxDate.setDate(maxDate.getDate() + maxValidityDays);
						return maxDate.toISOString().split('T')[0];
					})()}
					onChange={(e) => {
						const selectedDate = new Date(e.target.value);
						const today = new Date();
						today.setHours(0, 0, 0, 0);
						const maxDate = new Date();
						maxDate.setDate(maxDate.getDate() + maxValidityDays);
						maxDate.setHours(23, 59, 59, 999);

						if (selectedDate < today) {
							alert('Geçerlilik tarihi bugünden önce olamaz.');
							return;
						}

						if (selectedDate > maxDate) {
							alert(`Geçerlilik tarihi en fazla ${maxValidityDays} gün sonrasına kadar ayarlanabilir.`);
							return;
						}

						console.log('Date selected:', e.target.value);
						updateFields({ lastValidDate: e.target.value });
					}}
				/>
			</div>

			<label>Yıllık Vade Farkı Oranı:</label>
			<div className="custom-input">
				<input
					type="number"
					value={interestRate}
					readOnly
					placeholder="Otomatik atanır"
				/>
			</div>

			<label>Ödeme Planı:</label>
			<div className="custom-select">
				<select
					type="text"
					value={paymentType}
					onChange={(e) => updateFields({ paymentType: e.target.value })}
					placeholder='Örn: "Sabit Plan"'
				>
					<option value="" disabled>
						Bir plan tipi seçin
					</option>
					<option value="Sabit Plan">Sabit Plan</option>
					<option value="Özel Plan">Özel Plan</option>
				</select>
			</div>
		</form>
	);
}
